import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminTopline, Panel } from "../../components/console";
import { api, authHeaders, describeError } from "../../lib/api";
import type { ApiError, RefItem, StaffMember } from "../../lib/api";
import { getCurrentScope, getCurrentUserId } from "../../lib/auth";
import { canManageAccounts, currentPosition } from "../../lib/permissions";
import {
  MANAGEABLE_POSITIONS,
  POSITION_HINT_LO,
  POSITION_LABEL_LO,
  POSITION_PLACE,
  STAFF_POSITIONS,
  positionLabel,
  type PositionPlace,
  type StaffPosition,
} from "../../lib/positions";
import { initial } from "../../lib/format";

/** ตำแหน่ง → เขตที่ต้องผูก (ตำแหน่งที่จอนี้ไม่รู้จัก เช่น SA ไม่มีเขต) */
function placeOf(code: string): PositionPlace | null {
  return (POSITION_PLACE as Record<string, PositionPlace | undefined>)[code] ?? null;
}

function isStaffPosition(code: string): code is StaffPosition {
  return (STAFF_POSITIONS as string[]).includes(code);
}

async function fetchStaff(): Promise<StaffMember[]> {
  const { data, error, response } = await api.GET("/v1/admin/staff", {
    params: { query: { limit: 200 } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items.filter((u) => u.deleted_at === null);
}

/**
 * รายชื่อบ้าน — จำกัดเฉพาะเมืองของผู้ใช้เมื่อ scope เป็นระดับเมือง
 *
 * ผู้ใช้ระดับแขวง/ระบบไม่มีเมืองประจำ จึงโหลดทุกบ้าน เพื่อแสดงชื่อบ้านของพนักงานข้ามเมืองในตาราง
 */
async function fetchVillages(districtID: string | null): Promise<RefItem[]> {
  const { data, error, response } = await api.GET("/v1/reference/villages", {
    params: { query: districtID ? { district_id: districtID } : {} },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

/** ตาแสงที่ officer ผูกได้ — ระดับเมืองเห็นเฉพาะเมืองตัวเอง ระดับแขวง/ระบบเห็นทุกตาแสง */
async function fetchTasaengs(districtID: string | null): Promise<RefItem[]> {
  const { data, error, response } = await api.GET("/v1/reference/tasaengs", {
    params: { query: districtID ? { district_id: districtID } : {} },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

async function fetchProvinces(): Promise<RefItem[]> {
  const res = await api.GET("/v1/reference/provinces");
  // listProvinces ไม่ประกาศ 4xx ไว้ openapi-fetch จึงให้ error เป็น never — อ่านแบบเดียวกับ RequestDetailPage
  const { data, response } = res as { data?: { items: RefItem[] }; response: Response };
  if (!data) throw new Error(describeError(undefined, response.status));
  return data.items;
}

async function fetchDistricts(provinceID: string): Promise<RefItem[]> {
  const { data, error, response } = await api.GET("/v1/reference/districts", {
    params: { query: { province_id: provinceID } },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

export function DistrictStaffPage() {
  const qc = useQueryClient();
  const me = getCurrentUserId();
  const scope = getCurrentScope();
  const myPosition = currentPosition();
  // กฎ D5 (ADR-0030): ต้องมี CFG-11 และสร้าง/แก้ได้เฉพาะตำแหน่งที่ต่ำกว่า — server เช็คซ้ำพร้อม scope
  const assignable: StaffPosition[] =
    canManageAccounts() && myPosition ? (MANAGEABLE_POSITIONS[myPosition] ?? []) : [];
  // ต้องเลือกเมือง/แขวงให้บัญชีระดับเมือง/แขวง — ระดับแขวงเห็นแขวงตัวเอง, ระดับระบบเห็นทุกแขวง
  const managesFinance = assignable.some((p) => POSITION_PLACE[p] === "district" || POSITION_PLACE[p] === "province");

  const districtID = scope?.district_id ?? null;
  // ระดับแขวง/ระบบเห็นตาแสงหลายเมือง — ฟอร์มต้องกรองตาแสงด้วยแขวง → เมืองก่อน ไม่งั้นรายการยาวเกินหา
  const placeFilter = districtID === null;
  const needPlaces = managesFinance || placeFilter;
  const staff = useQuery({ queryKey: ["staff"], queryFn: fetchStaff });
  const villages = useQuery({
    queryKey: ["reference", "villages", districtID],
    queryFn: () => fetchVillages(districtID),
  });
  const tasaengs = useQuery({
    queryKey: ["reference", "tasaengs", districtID],
    queryFn: () => fetchTasaengs(districtID),
  });
  const provinces = useQuery({
    queryKey: ["reference", "provinces"],
    queryFn: fetchProvinces,
    enabled: needPlaces,
    staleTime: Infinity,
  });
  const ownProvinceID = scope?.level === "province" ? (scope.province_id ?? null) : null;
  const provinceIDs = useMemo(
    () => (ownProvinceID ? [ownProvinceID] : (provinces.data ?? []).map((p) => p.id)),
    [ownProvinceID, provinces.data],
  );
  // เมืองทุกเมืองในแขวงที่จัดการได้ — ใช้ทั้ง dropdown และแสดงชื่อเมืองของการเงินในตาราง
  const districtQueries = useQueries({
    queries: needPlaces
      ? provinceIDs.map((id) => ({
          queryKey: ["reference", "districts", id],
          queryFn: () => fetchDistricts(id),
          staleTime: Infinity,
        }))
      : [],
  });
  const districtsByProvince = useMemo(() => {
    const m = new Map<string, RefItem[]>();
    provinceIDs.forEach((id, i) => {
      const items = districtQueries[i]?.data;
      if (items) m.set(id, items);
    });
    return m;
  }, [provinceIDs, districtQueries]);

  const [form, setForm] = useState<{ mode: "create" } | { mode: "edit"; member: StaffMember } | null>(null);
  const [closing, setClosing] = useState<StaffMember | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const list of [villages.data, tasaengs.data, provinces.data]) {
      for (const it of list ?? []) m.set(it.id, it.name_lo);
    }
    for (const list of districtsByProvince.values()) {
      for (const it of list) m.set(it.id, it.name_lo);
    }
    return m;
  }, [villages.data, tasaengs.data, provinces.data, districtsByProvince]);

  const deactivate = useMutation({
    mutationFn: async (id: string) => {
      const { error: err, response } = await api.DELETE("/v1/admin/staff/{id}", {
        params: { path: { id } },
        headers: authHeaders(),
      });
      if (err) throw new Error(describeError(err, response.status));
    },
    onSuccess: () => {
      setError(null);
      setClosing(null);
      void qc.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e: Error) => {
      setClosing(null);
      setError(e.message);
    },
  });

  // แก้/ปิดได้เฉพาะบัญชีที่ตำแหน่งอยู่ในขั้นที่จัดการได้ และไม่ใช่บัญชีตัวเอง (server ปฏิเสธอยู่แล้ว แค่ไม่โชว์ปุ่มที่กดไม่ผ่าน)
  const canManage = (u: StaffMember) => u.id !== me && (assignable as string[]).includes(u.position_code);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = staff.data ?? [];
    if (q === "") return all;
    return all.filter((u) =>
      [u.full_name, u.phone ?? "", u.email ?? ""].some((v) => v.toLowerCase().includes(q)),
    );
  }, [staff.data, query]);

  return (
    <div>
      <AdminTopline
        title="ບັນຊີຕາແສງ/ນາຍບ້ານ"
        subtitle="ບັນຊີໃໝ່ຈະຕັ້ງລະຫັດ PIN ດ້ວຍຕົນເອງຜ່ານ OTP"
        roleLabel={myPosition ? positionLabel(myPosition) : undefined}
        actions={
          assignable.length > 0 ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => {
                setForm({ mode: "create" });
                setError(null);
              }}
            >
              + ເພີ່ມບັນຊີ
            </button>
          ) : null
        }
      />

      {staff.error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {staff.error.message}
        </div>
      ) : null}
      {error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          {error}
        </div>
      ) : null}

      <Panel title="ບັນຊີພະນັກງານໃນເຂດຂອງທ່ານ">
        <input
          className="input-field"
          style={{ maxWidth: "22rem", marginBottom: "var(--space-3)" }}
          placeholder="ຄົ້ນຫາຊື່, ເບີໂທ ຫຼື ອີເມວ"
          aria-label="ຄົ້ນຫາບັນຊີ"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
        />
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ຊື່</th>
                <th>ຕຳແໜ່ງ</th>
                <th>ເຂດຮັບຜິດຊອບ</th>
                <th>ສະຖານະ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {staff.isPending ? (
                <tr>
                  <td colSpan={5} className="data-table__empty">
                    ກຳລັງໂຫຼດ…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="data-table__empty">
                    {query.trim() ? "ບໍ່ພົບບັນຊີທີ່ກົງກັບການຄົ້ນຫາ" : "ຍັງບໍ່ມີບັນຊີພະນັກງານໃນເຂດນີ້"}
                  </td>
                </tr>
              ) : (
                rows.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="row" style={{ gap: "var(--space-2-5)" }}>
                        <span className="avatar">{initial(u.full_name)}</span>
                        <div style={{ minWidth: 0 }}>
                          <div className="body-sm" style={{ fontWeight: 500 }}>
                            {u.full_name}
                            {u.id === me ? <span className="caption"> (ທ່ານ)</span> : null}
                          </div>
                          <div className="caption num">{u.phone ?? u.email ?? "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>{positionLabel(u.position_code)}</div>
                      {u.can_sign ? <div className="caption">ເຊັນເອກະສານໄດ້</div> : null}
                    </td>
                    <td>
                      <StaffPlace member={u} names={names} />
                    </td>
                    <td>
                      {/* mockup เขียนว่า "Active" เป็นภาษาอังกฤษ แต่ ADR-0011 บอกว่า
                          UI ต้องเป็นภาษาลาวล้วน จึงใช้ ໃຊ້ງານຢູ່ แทน */}
                      {u.pending_activation ? (
                        <span className="status-badge status-badge--pending">ລໍຖ້າຢືນຢັນ</span>
                      ) : (
                        <span className="status-badge status-badge--success">ໃຊ້ງານຢູ່</span>
                      )}
                    </td>
                    <td>
                      {canManage(u) ? (
                        <div className="row" style={{ gap: "var(--space-3)", flexWrap: "nowrap" }}>
                          <button
                            type="button"
                            className="link-action"
                            onClick={() => {
                              setForm({ mode: "edit", member: u });
                              setError(null);
                            }}
                          >
                            ແກ້ໄຂ
                          </button>
                          <button
                            type="button"
                            className="link-action"
                            onClick={() => {
                              setClosing(u);
                              setError(null);
                            }}
                          >
                            ປິດບັນຊີ
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {form ? (
        <StaffFormModal
          member={form.mode === "edit" ? form.member : null}
          assignable={assignable}
          tasaengs={tasaengs.data ?? []}
          villages={villages.data ?? []}
          ownTasaengID={scope?.level === "tasaeng" ? (scope.tasaeng_id ?? null) : null}
          provinces={
            ownProvinceID ? (provinces.data ?? []).filter((p) => p.id === ownProvinceID) : (provinces.data ?? [])
          }
          districtsByProvince={districtsByProvince}
          placeFilter={placeFilter}
          onClose={() => {
            setForm(null);
          }}
          onDone={() => {
            setForm(null);
            void qc.invalidateQueries({ queryKey: ["staff"] });
          }}
        />
      ) : null}

      {closing ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ປິດບັນຊີ">
          <div className="modal modal--sm">
            <div className="modal__header">
              <h2 className="modal__title">ປິດບັນຊີ {closing.full_name}?</h2>
              <p className="caption">
                ເຈົ້າຕົວຈະຖືກອອກຈາກລະບົບທຸກເຄື່ອງທັນທີ ແລະ ເຂົ້າລະບົບບໍ່ໄດ້ອີກ — ຄຳຮ້ອງ ແລະ ປະຫວັດທີ່ເຄີຍເຮັດຍັງຄົງຢູ່
              </p>
            </div>
            <div className="modal__actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setClosing(null);
                }}
              >
                ຍົກເລີກ
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={deactivate.isPending}
                onClick={() => {
                  deactivate.mutate(closing.id);
                }}
              >
                {deactivate.isPending ? "ກຳລັງປິດ…" : "ປິດບັນຊີ"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * ข้อความ error ตอนบันทึกบัญชี — บ้านหนึ่งมีนายบ้านได้คนเดียว (ADR-0031, unique index
 * `users_one_village_head_per_village`): server ตอบ 409 `village_head_exists: <ข้อความลาว>` แสดงแค่ส่วนภาษาลาว
 */
function staffSaveError(err: ApiError | undefined, status: number): string {
  const message = err?.error?.message ?? "";
  if (status === 409 && message.includes("village_head_exists")) {
    const lao = message.replace(/^.*village_head_exists:\s*/, "").trim();
    return lao || "ບ້ານນີ້ມີນາຍບ້ານແລ້ວ — ປິດ ຫຼື ຍ້າຍບັນຊີນາຍບ້ານຄົນເກົ່າກ່ອນ";
  }
  if (status === 409 && message.includes("identifier_taken")) return "ເບີໂທ/ອີເມວນີ້ມີບັນຊີອື່ນໃຊ້ຢູ່ແລ້ວ";
  if (status === 409 && message.includes("account_closed"))
    return "ເບີໂທ/ອີເມວນີ້ເປັນຂອງບັນຊີທີ່ປິດແລ້ວ ໃຊ້ຄືນບໍ່ໄດ້";
  return describeError(err, status);
}

/**
 * เขตที่บัญชีรับผิดชอบตามตำแหน่ง (ADR-0030): บ้าน (ນາຍບ້ານ) · ตาแสง (TH/TK/TR/TF) · เมือง (DO) · แขวง (PO/AD)
 * officer เดิมที่ยังผูกกับบ้านเดียวแสดงชื่อบ้าน พร้อมบอกว่ายังไม่ได้ผูกตาแสง
 */
function StaffPlace({ member, names }: { member: StaffMember; names: Map<string, string> }) {
  const name = (id: string | null) => (id ? (names.get(id) ?? "…") : null);
  switch (placeOf(member.position_code)) {
    case "village":
      return <>{member.village_id ? `ບ້ານ${name(member.village_id) ?? ""}` : "—"}</>;
    case "tasaeng":
      if (member.scope_tasaeng_id) return <>{names.get(member.scope_tasaeng_id) ?? "ຕາແສງທີ່ຖືກລຶບແລ້ວ"}</>;
      return (
        <div>
          <div>{name(member.village_id) ?? "—"}</div>
          <div className="caption">ຍັງບໍ່ໄດ້ຜູກກັບຕາແສງ</div>
        </div>
      );
    case "district":
      return <>{member.scope_district_id ? `ເມືອງ${name(member.scope_district_id) ?? ""}` : "—"}</>;
    case "province":
      return <>{name(member.scope_province_id) ?? "—"}</>;
    default:
      return <>—</>;
  }
}

/**
 * ฟอร์มเดียวทั้งเปิดบัญชีและแก้ไข — แก้ไขส่งเฉพาะช่องที่เปลี่ยน (PATCH)
 *
 * เลือก **ตำแหน่ง** (ADR-0030) ไม่ใช่ role: role ตามไปเป็น base role ของตำแหน่ง และสิทธิ์เซ็นตามตำแหน่ง
 * (ຫົວໜ້າຕາແສງ = ເຊັນໄດ້, ตำแหน่งอื่นไม่ได้) จึงไม่มีช่อง can_sign แยกอีก
 */
function StaffFormModal({
  member,
  assignable,
  tasaengs,
  villages,
  ownTasaengID,
  provinces,
  districtsByProvince,
  placeFilter,
  onClose,
  onDone,
}: {
  member: StaffMember | null;
  assignable: StaffPosition[];
  tasaengs: RefItem[];
  villages: RefItem[];
  ownTasaengID: string | null;
  provinces: RefItem[];
  districtsByProvince: Map<string, RefItem[]>;
  /** true = ตาแสงมาจากหลายเมือง ให้เลือกแขวง → เมืองก่อนเลือกตาแสง */
  placeFilter: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const editing = member !== null;
  // ເບີໂທ/ອີເມວເຂົ້າລະບົບ: ตอนแก้เปลี่ยนได้เฉพาะ super admin และไม่ใช่บัญชีตัวเอง (server ตรวจซ้ำ) —
  // เปลี่ยนแล้ว PIN ถูกล้าง + ทุกเครื่องออกจากระบบ เจ้าตัวตั้ง PIN ใหม่ผ่าน OTP ที่ช่องทางใหม่
  const initialContact = member ? (member.phone ?? member.email ?? "") : "";
  const canEditContact = !editing || (currentPosition() === "SA" && member.id !== getCurrentUserId());
  const initialPosition: StaffPosition =
    member && isStaffPosition(member.position_code) ? member.position_code : (assignable[0] ?? "TK");
  const provinceOfDistrict = (districtID: string | null) => {
    if (!districtID) return "";
    for (const [pid, list] of districtsByProvince) if (list.some((d) => d.id === districtID)) return pid;
    return "";
  };
  const tasaengOfVillage = (villageID: string | null | undefined) =>
    villageID ? (villages.find((v) => v.id === villageID)?.tasaeng_id ?? "") : "";
  const [values, setValues] = useState({
    full_name: member?.full_name ?? "",
    contact: initialContact,
    position: initialPosition,
    tasaeng_id: member?.scope_tasaeng_id ?? "",
    village_id: member?.village_id ?? "",
    // ตัวช่วยกรองบ้านของนายบ้าน (ไม่ได้ส่งไป server) — ผู้ใช้ระดับตาแสงถูกล็อกไว้ที่ตาแสงตัวเอง
    village_tasaeng_id: ownTasaengID ?? "",
    province_id: member?.scope_province_id ?? "",
    district_id: member?.scope_district_id ?? "",
    // ตัวกรองตาแสง (ไม่ได้ส่งไป server) — ว่าง = ใช้แขวง/เมืองของตาแสงที่เลือกอยู่
    filter_province_id: "",
    filter_district_id: "",
  });
  const [error, setError] = useState<string | null>(null);
  // แขวงของเมืองที่ตั้งไว้หาตอน render (รายชื่อเมืองอาจโหลดเสร็จหลังเปิดฟอร์ม) · ระดับแขวงมีแขวงเดียว — เลือกให้เลย
  const provinceID =
    values.province_id ||
    provinceOfDistrict(values.district_id || null) ||
    (provinces.length === 1 ? (provinces[0]?.id ?? "") : "");
  const districts = provinceID ? (districtsByProvince.get(provinceID) ?? []) : [];
  const place = POSITION_PLACE[values.position];
  const initialPlace = POSITION_PLACE[initialPosition];
  const contactChanged = editing && canEditContact && values.contact.trim() !== initialContact;
  // บ้านของนายบ้านเลือกจากตาแสง — ตาแสงของบ้านเดิม (แก้ไข) หาตอน render เพราะรายชื่อบ้านอาจโหลดทีหลัง
  const villageTasaeng = ownTasaengID ?? (values.village_tasaeng_id || tasaengOfVillage(values.village_id));
  const villageChoices = villageTasaeng ? villages.filter((v) => v.tasaeng_id === villageTasaeng) : [];
  // ตัวกรองแขวง/เมืองของช่องตาแสง — ตอนแก้ไขเริ่มจากเมืองของตาแสงเดิม (หาตอน render เพราะรายการโหลดทีหลังได้)
  const pickedTasaeng = place === "village" ? villageTasaeng : values.tasaeng_id;
  const filterDistrictID =
    values.filter_district_id || (tasaengs.find((t) => t.id === pickedTasaeng)?.parent_id ?? "");
  const filterProvinceID =
    values.filter_province_id ||
    provinceOfDistrict(filterDistrictID || null) ||
    (provinces.length === 1 ? (provinces[0]?.id ?? "") : "");
  const filterDistricts = filterProvinceID ? (districtsByProvince.get(filterProvinceID) ?? []) : [];
  const tasaengCount = new Map<string, number>();
  for (const t of tasaengs) if (t.parent_id) tasaengCount.set(t.parent_id, (tasaengCount.get(t.parent_id) ?? 0) + 1);
  const tasaengChoices = !placeFilter
    ? tasaengs
    : filterDistrictID
      ? tasaengs.filter((t) => t.parent_id === filterDistrictID)
      : [];
  const tasaengHint = (picked: string) =>
    placeFilter && filterDistrictID === ""
      ? "ເລືອກແຂວງ ແລະ ເມືອງກ່ອນ"
      : tasaengChoices.length === 0
        ? placeFilter
          ? "ເມືອງນີ້ຍັງບໍ່ມີຕາແສງ — ຜູ້ບໍລິຫານລະບົບຕ້ອງເພີ່ມຕາແສງກ່ອນ"
          : "ຍັງບໍ່ມີຕາແສງໃນເຂດນີ້ — ຜູ້ບໍລິຫານລະບົບຕ້ອງເພີ່ມຕາແສງກ່ອນ"
        : picked;
  // เปลี่ยนแขวง/เมืองของตัวกรอง = ล้างตาแสง/บ้านที่เลือกไว้ (ไม่งั้นค่าเดิมดึงตัวกรองกลับ)
  const clearTasaengPick = { tasaeng_id: "", village_tasaeng_id: "", village_id: "" };
  const placeFilterFields = placeFilter ? (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-3)" }}>
      <div>
        <label className="form-field-label" htmlFor="staff-filter-province">
          ແຂວງ
        </label>
        <select
          id="staff-filter-province"
          className="input-field"
          value={filterProvinceID}
          disabled={provinces.length === 0}
          onChange={(e) => {
            setValues({ ...values, ...clearTasaengPick, filter_province_id: e.target.value, filter_district_id: "" });
          }}
        >
          <option value="">— ເລືອກແຂວງ —</option>
          {provinces.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name_lo}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="form-field-label" htmlFor="staff-filter-district">
          ເມືອງ
        </label>
        <select
          id="staff-filter-district"
          className="input-field"
          value={filterDistrictID}
          disabled={filterDistricts.length === 0}
          onChange={(e) => {
            setValues({
              ...values,
              ...clearTasaengPick,
              filter_province_id: filterProvinceID,
              filter_district_id: e.target.value,
            });
          }}
        >
          <option value="">— ເລືອກເມືອງ —</option>
          {filterDistricts.map((d) => {
            const n = tasaengCount.get(d.id) ?? 0;
            return (
              <option key={d.id} value={d.id}>
                {d.name_lo} {n > 0 ? `(${String(n)} ຕາແສງ)` : "(ບໍ່ມີຕາແສງ)"}
              </option>
            );
          })}
        </select>
      </div>
    </div>
  ) : null;

  const save = useMutation({
    mutationFn: async () => {
      const position = values.position;
      const scopeFields = {
        tasaeng_id: place === "tasaeng" && values.tasaeng_id !== "" ? values.tasaeng_id : null,
        district_id: place === "district" && values.district_id !== "" ? values.district_id : null,
        province_id: place === "province" && provinceID !== "" ? provinceID : null,
      };
      const villageID = place === "village" && values.village_id !== "" ? values.village_id : null;
      if (!member) {
        const contact = values.contact.trim();
        const { data, error: err, response } = await api.POST("/v1/admin/staff", {
          body: {
            full_name: values.full_name.trim(),
            ...(contact.includes("@") ? { email: contact } : { phone: contact }),
            position_code: position,
            ...scopeFields,
            ...(villageID ? { village_id: villageID } : {}),
          },
          headers: authHeaders(),
        });
        if (err || !data) throw new Error(staffSaveError(err, response.status));
        return;
      }
      // ส่งเฉพาะที่เปลี่ยน — เปลี่ยนตำแหน่งข้ามเขต (role ใหม่) ต้องส่งเขตใหม่ไปด้วยเสมอ (server ล้างเขตเดิม)
      const positionChanged = position !== initialPosition;
      const placeChanged = place !== initialPlace;
      const body: {
        full_name?: string;
        position_code?: StaffPosition;
        village_id?: string | null;
        tasaeng_id?: string | null;
        district_id?: string | null;
        province_id?: string | null;
        identifier?: string;
      } = {};
      if (values.full_name.trim() !== member.full_name) body.full_name = values.full_name.trim();
      if (contactChanged) body.identifier = values.contact.trim();
      if (positionChanged) body.position_code = position;
      if (place === "tasaeng" && (placeChanged || scopeFields.tasaeng_id !== member.scope_tasaeng_id))
        body.tasaeng_id = scopeFields.tasaeng_id;
      if (place === "district" && (placeChanged || scopeFields.district_id !== member.scope_district_id))
        body.district_id = scopeFields.district_id;
      if (place === "province" && (placeChanged || scopeFields.province_id !== member.scope_province_id))
        body.province_id = scopeFields.province_id;
      if (place === "village" && (placeChanged || villageID !== member.village_id)) body.village_id = villageID;
      if (Object.keys(body).length === 0) return;
      const { error: err, response } = await api.PATCH("/v1/admin/staff/{id}", {
        params: { path: { id: member.id } },
        body,
        headers: authHeaders(),
      });
      if (err) throw new Error(staffSaveError(err, response.status));
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (values.full_name.trim() === "") {
      setError("ຕ້ອງລະບຸຊື່");
      return;
    }
    if (values.contact.trim() === "" && (!editing || canEditContact)) {
      setError("ຕ້ອງລະບຸເບີໂທ ຫຼື ອີເມວ — ລະບົບຈະສົ່ງ OTP ໄປທີ່ນີ້ເພື່ອຕັ້ງ PIN");
      return;
    }
    // officer เดิมที่ผูกกับบ้าน (ยังไม่มีตาแสง) แก้ชื่อ/ตำแหน่งในตาแสงได้โดยไม่บังคับเลือกตาแสง
    const legacyOfficer = editing && initialPlace === "tasaeng" && !member.scope_tasaeng_id;
    if (place === "tasaeng" && values.tasaeng_id === "" && !(legacyOfficer && initialPlace === place)) {
      setError("ເຈົ້າໜ້າທີ່ຕາແສງຕ້ອງສັງກັດຕາແສງໃດໜຶ່ງ");
      return;
    }
    if (place === "village" && values.village_id === "") {
      setError("ນາຍບ້ານຕ້ອງສັງກັດບ້ານໃດໜຶ່ງ");
      return;
    }
    if (place === "district" && values.district_id === "") {
      setError("ເຈົ້າໜ້າທີ່ເມືອງຕ້ອງສັງກັດເມືອງໃດໜຶ່ງ");
      return;
    }
    if (place === "province" && provinceID === "") {
      setError("ຕຳແໜ່ງລະດັບແຂວງຕ້ອງສັງກັດແຂວງໃດໜຶ່ງ");
      return;
    }
    save.mutate();
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={editing ? "ແກ້ໄຂບັນຊີ" : "ເພີ່ມບັນຊີ"}>
      <form className="modal modal--sm" onSubmit={submit}>
        <div className="modal__header">
          <h2 className="modal__title">{editing ? "ແກ້ໄຂບັນຊີພະນັກງານ" : "ເພີ່ມບັນຊີພະນັກງານ"}</h2>
          {/* ผู้สร้างไม่ตั้ง PIN ให้ — ต้องบอกให้ชัดว่าขั้นถัดไปคืออะไร */}
          <p className="caption">
            {!editing
              ? "ບັນຊີຈະຢູ່ສະຖານະ ລໍຖ້າຢືນຢັນ ຈົນກວ່າເຈົ້າຕົວຈະຕັ້ງ PIN ເອງ"
              : canEditContact
                ? "ປ່ຽນເບີໂທ/ອີເມວເຂົ້າລະບົບໄດ້ — ເຈົ້າຕົວຕ້ອງຕັ້ງ PIN ໃໝ່ຜ່ານ OTP"
                : "ເບີໂທ/ອີເມວທີ່ໃຊ້ເຂົ້າລະບົບ ແກ້ໄດ້ສະເພາະຜູ້ຄຸ້ມຄອງລະບົບ"}
          </p>
        </div>

        {error ? <div className="alert-error">{error}</div> : null}

        <div className="stack">
          <div>
            <label className="form-field-label" htmlFor="staff-name">
              ຊື່ ແລະ ນາມສະກຸນ <span className="required">*</span>
            </label>
            <input
              id="staff-name"
              className="input-field"
              value={values.full_name}
              onChange={(e) => {
                setValues({ ...values, full_name: e.target.value });
              }}
            />
          </div>
          <div>
            <label className="form-field-label" htmlFor="staff-contact">
              ເບີໂທ ຫຼື ອີເມວ {canEditContact ? <span className="required">*</span> : null}
            </label>
            <input
              id="staff-contact"
              className="input-field num"
              placeholder="+85620…"
              disabled={!canEditContact}
              value={values.contact}
              onChange={(e) => {
                setValues({ ...values, contact: e.target.value });
              }}
            />
            {contactChanged ? (
              <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                ບັນທຶກແລ້ວ ທຸກເຄື່ອງຂອງບັນຊີນີ້ຈະອອກຈາກລະບົບ ແລະ ເຈົ້າຕົວຕ້ອງຕັ້ງ PIN ໃໝ່ຜ່ານ OTP ທີ່ເບີ/ອີເມວໃໝ່
              </p>
            ) : null}
          </div>
          <div>
            <label className="form-field-label" htmlFor="staff-position">
              ຕຳແໜ່ງ <span className="required">*</span>
            </label>
            <select
              id="staff-position"
              className="input-field"
              value={values.position}
              onChange={(e) => {
                setValues({ ...values, position: e.target.value as StaffPosition });
              }}
            >
              {assignable.map((p) => (
                <option key={p} value={p}>
                  {POSITION_LABEL_LO[p]}
                </option>
              ))}
            </select>
            <p className="caption" style={{ marginTop: "var(--space-1)" }}>
              {POSITION_HINT_LO[values.position]}
              {values.position === "TH" ? " · ເຊັນເອກະສານໄດ້" : ""}
            </p>
            {editing && place !== initialPlace ? (
              <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                ປ່ຽນຕຳແໜ່ງຂ້າມລະດັບແລ້ວ ເຂດເກົ່າຈະຖືກລ້າງ — ເລືອກເຂດຂອງຕຳແໜ່ງໃໝ່ຂ້າງລຸ່ມ
              </p>
            ) : null}
          </div>

          {place === "tasaeng" ? placeFilterFields : null}
          {place === "tasaeng" ? (
            <div>
              <label className="form-field-label" htmlFor="staff-tasaeng">
                ຕາແສງ <span className="required">*</span>
              </label>
              <select
                id="staff-tasaeng"
                className="input-field"
                value={values.tasaeng_id}
                disabled={tasaengChoices.length === 0}
                onChange={(e) => {
                  setValues({ ...values, tasaeng_id: e.target.value });
                }}
              >
                <option value="">— ເລືອກຕາແສງ —</option>
                {tasaengChoices.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name_lo} ({t.code})
                  </option>
                ))}
              </select>
              <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                {tasaengHint("ເຈົ້າໜ້າທີ່ຈະເຫັນຄຳຮ້ອງຂອງທຸກບ້ານທີ່ຂຶ້ນກັບຕາແສງນີ້")}
              </p>
            </div>
          ) : null}

          {place === "village" ? (
            <>
              {ownTasaengID === null ? placeFilterFields : null}
              {ownTasaengID === null ? (
                <div>
                  <label className="form-field-label" htmlFor="staff-village-tasaeng">
                    ຕາແສງຂອງບ້ານ <span className="required">*</span>
                  </label>
                  <select
                    id="staff-village-tasaeng"
                    className="input-field"
                    value={villageTasaeng}
                    disabled={tasaengChoices.length === 0}
                    onChange={(e) => {
                      setValues({ ...values, village_tasaeng_id: e.target.value, village_id: "" });
                    }}
                  >
                    <option value="">— ເລືອກຕາແສງ —</option>
                    {tasaengChoices.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name_lo} ({t.code})
                      </option>
                    ))}
                  </select>
                  {placeFilter && tasaengHint("") !== "" ? (
                    <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                      {tasaengHint("")}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div>
                <label className="form-field-label" htmlFor="staff-village">
                  ບ້ານ <span className="required">*</span>
                </label>
                <select
                  id="staff-village"
                  className="input-field"
                  value={values.village_id}
                  disabled={villageChoices.length === 0}
                  onChange={(e) => {
                    setValues({ ...values, village_id: e.target.value });
                  }}
                >
                  <option value="">— ເລືອກບ້ານ —</option>
                  {villageChoices.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name_lo}
                    </option>
                  ))}
                </select>
                <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                  {villageTasaeng === ""
                    ? "ເລືອກຕາແສງກ່ອນ"
                    : villageChoices.length === 0
                      ? "ຕາແສງນີ້ຍັງບໍ່ມີບ້ານ"
                      : "ນາຍບ້ານໃຊ້ແອັບມືຖື ແລະ ເຫັນສະເພາະບ້ານຂອງຕົນ"}
                </p>
              </div>
            </>
          ) : null}

          {(place === "district" || place === "province") && provinces.length > 1 ? (
            <div>
              <label className="form-field-label" htmlFor="staff-province">
                ແຂວງ <span className="required">*</span>
              </label>
              <select
                id="staff-province"
                className="input-field"
                value={provinceID}
                onChange={(e) => {
                  setValues({ ...values, province_id: e.target.value, district_id: "" });
                }}
              >
                <option value="">— ເລືອກແຂວງ —</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name_lo}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {place === "district" ? (
            <div>
              <label className="form-field-label" htmlFor="staff-district">
                ເມືອງ <span className="required">*</span>
              </label>
              <select
                id="staff-district"
                className="input-field"
                value={values.district_id}
                disabled={districts.length === 0}
                onChange={(e) => {
                  setValues({ ...values, district_id: e.target.value });
                }}
              >
                <option value="">— ເລືອກເມືອງ —</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name_lo}
                  </option>
                ))}
              </select>
              <p className="caption" style={{ marginTop: "var(--space-1)" }}>
                ເຫັນຂໍ້ມູນ ແລະ ລາຍງານຂອງທຸກຕາແສງໃນເມືອງນີ້
              </p>
            </div>
          ) : null}
        </div>

        <div className="modal__actions" style={{ marginTop: "var(--space-6)" }}>
          <button type="button" className="btn-secondary" onClick={onClose}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "ກຳລັງບັນທຶກ…" : editing ? "ບັນທຶກ" : "ເປີດບັນຊີ"}
          </button>
        </div>
      </form>
    </div>
  );
}
